/**
 * Question Wheels Panel
 *
 * Browse and manage question wheels for the site's sector
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { authFetch } from '@/utils/auth-fetch';
import { Loader2, Eye } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

interface QuestionWheel {
  id: string;
  name: string;
  description: string;
  wheel_type: string;
  question_count: number;
  language: string;
  status: string;
}

interface QuestionWheelsPanelProps {
  siteId: string;
  sectorId?: number | null; // sites.sector_id is an integer; only interpolated into the URL
}

export function QuestionWheelsPanel({ siteId, sectorId }: QuestionWheelsPanelProps) {
  const [wheels, setWheels] = useState<QuestionWheel[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    loadWheels();
  }, [sectorId]);

  const loadWheels = async () => {
    if (!sectorId) {
      setWheels([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await authFetch(`/sectors/${sectorId}/question-wheels`);
      const data = await response.json();

      if (data.success) {
        setWheels(data.wheels || []);
      }
    } catch (error) {
      console.error('Failed to load question wheels:', error);
      toast({
        title: 'Error',
        description: 'Failed to load question wheels',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Available Question Wheels</CardTitle>
        <CardDescription>
          Pre-built question sets for your industry sector
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : wheels.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No question wheels available for your sector
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {wheels.map((wheel) => (
              <Card key={wheel.id}>
                <CardHeader>
                  <CardTitle className="text-base">{wheel.name}</CardTitle>
                  <div className="flex gap-2 mt-2">
                    <Badge variant="secondary">{wheel.wheel_type}</Badge>
                    <Badge variant="outline">{wheel.language}</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">
                    {wheel.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">
                      {wheel.question_count} questions
                    </span>
                    <Button size="sm" variant="outline">
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
